import React from 'react'

interface SubmissionPieChartProps {
  completeCount: number
  inProgressCount: number
  notStartedCount: number
}

export const SubmissionPieChart: React.FC<SubmissionPieChartProps> = ({
  completeCount,
  inProgressCount,
  notStartedCount
}) => {
  const total = completeCount + inProgressCount + notStartedCount
  
  // Calculate SVG Pie portions
  const pctComplete = total > 0 ? (completeCount / total) * 100 : 0
  const pctInProgress = total > 0 ? (inProgressCount / total) * 100 : 0
  const pctNotStarted = total > 0 ? (notStartedCount / total) * 100 : 0

  // SVG Circle stroke dash calculations
  const radius = 50
  const circumference = 2 * Math.PI * radius
  
  const strokeComplete = (pctComplete / 100) * circumference
  const strokeInProgress = (pctInProgress / 100) * circumference
  const strokeNotStarted = (pctNotStarted / 100) * circumference

  const offsetComplete = 0
  const offsetInProgress = strokeComplete
  const offsetNotStarted = strokeComplete + strokeInProgress

  return (
    <div style={{
      background: 'var(--bg-card, #ffffff)',
      border: '1px solid var(--border-color, rgba(0, 0, 0, 0.1))',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '260px'
    }}>
      <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, width: '100%', textAlign: 'left' }}>
        สัดส่วนสถานะการส่งคะแนน
      </h4>

      {total === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px', color: 'var(--text-secondary)' }}>
          ไม่มีข้อมูลสรุป
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', width: '100%', flexWrap: 'wrap', justifyContent: 'center' }}>
          {/* SVG Pie container */}
          <div style={{ position: 'relative', width: '130px', height: '130px' }}>
            <svg width="100%" height="100%" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
              {/* Not Started Arc */}
              {pctNotStarted > 0 && (
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="transparent"
                  stroke="#f1f5f9"
                  strokeWidth="14"
                  strokeDasharray={`${strokeNotStarted} ${circumference}`}
                  strokeDashoffset={-offsetNotStarted}
                  style={{ transition: 'stroke-dasharray 0.3s ease' }}
                />
              )}

              {/* In Progress Arc */}
              {pctInProgress > 0 && (
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="transparent"
                  stroke="#3b82f6"
                  strokeWidth="14"
                  strokeDasharray={`${strokeInProgress} ${circumference}`}
                  strokeDashoffset={-offsetInProgress}
                  style={{ transition: 'stroke-dasharray 0.3s ease' }}
                />
              )}

              {/* Complete Arc */}
              {pctComplete > 0 && (
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="transparent"
                  stroke="#10b981"
                  strokeWidth="14"
                  strokeDasharray={`${strokeComplete} ${circumference}`}
                  strokeDashoffset={-offsetComplete}
                  style={{ transition: 'stroke-dasharray 0.3s ease' }}
                />
              )}
            </svg>

            {/* Total count indicator in center */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none'
            }}>
              <div style={{ fontSize: '20px', fontWeight: 800 }}>{total}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary, #64748b)' }}>ทั้งหมด</div>
            </div>
          </div>

          {/* Legends */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minWidth: '140px' }}>
            {/* Complete Legend */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#10b981' }} />
                <span>ส่งครบแล้ว</span>
              </div>
              <strong style={{ marginLeft: '8px' }}>{completeCount} ({pctComplete.toFixed(0)}%)</strong>
            </div>

            {/* In Progress Legend */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#3b82f6' }} />
                <span>กำลังกรอก</span>
              </div>
              <strong style={{ marginLeft: '8px' }}>{inProgressCount} ({pctInProgress.toFixed(0)}%)</strong>
            </div>

            {/* Not Started Legend */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#e2e8f0' }} />
                <span>ยังไม่ส่ง</span>
              </div>
              <strong style={{ marginLeft: '8px' }}>{notStartedCount} ({pctNotStarted.toFixed(0)}%)</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
